import React, { useCallback, useEffect, useId, useState } from 'react';
import { LazyTooltip } from '../design-system/Tooltip';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { JD_MORE_INFO_CUSTOM_TRIGGER } from '../utils/job-detail/job-detail-more-info-affordance';

export interface SalaryInferenceExplainerProps {
  /** Plain-language explanation shown in tooltip (desktop) or below trigger (mobile tap). */
  explanationText: string;
  children: React.ReactNode;
  /** Additional classes on the outer wrapper (e.g. hit card alignment). */
  className?: string;
  /** Overrides the default dotted-underline trigger style (JD_MORE_INFO_CUSTOM_TRIGGER) — e.g. JD_MORE_INFO_CUSTOM_TRIGGER_BARE for a compact figure that supplies its own indicator. */
  triggerClassName?: string;
}

/**
 * Wraps a salary amount so users can discover why it is estimated or unverified:
 * hover/focus on large screens, tap to expand on smaller viewports.
 */
export const SalaryInferenceExplainer: React.FC<SalaryInferenceExplainerProps> = ({
  explanationText,
  children,
  className = '',
  triggerClassName,
}) => {
  const [mounted, setMounted] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isLg = useMediaQuery('(min-width: 1024px)', false);
  const detailId = useId();

  useEffect(() => {
    setMounted(true);
  }, []);

  const toggleMobile = useCallback(() => {
    setMobileOpen((o) => !o);
  }, []);

  if (!explanationText.trim()) {
    return <span className={className}>{children}</span>;
  }

  if (!mounted) {
    return <span className={className}>{children}</span>;
  }

  const triggerClass = triggerClassName ?? JD_MORE_INFO_CUSTOM_TRIGGER;

  if (isLg) {
    return (
      <LazyTooltip
        position="bottom"
        surface="popover"
        content={<span className="block text-left">{explanationText}</span>}
      >
        <span tabIndex={0} className={`${triggerClass} ${className}`.trim()}>
          {children}
        </span>
      </LazyTooltip>
    );
  }

  return (
    <span className={`inline-flex flex-col items-start min-w-0 ${className}`.trim()}>
      <button
        type="button"
        className={`${triggerClass} text-left bg-transparent p-0 font-inherit`}
        aria-expanded={mobileOpen}
        aria-controls={detailId}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleMobile();
        }}
      >
        {children}
      </button>
      {mobileOpen ? (
        <span
          id={detailId}
          role="note"
          className="mt-1 max-w-[min(100%,20rem)] text-xs font-normal text-gray-600 dark:text-gray-400 leading-snug"
        >
          {explanationText}
        </span>
      ) : null}
    </span>
  );
};
